import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { colors } from '@anyroad/theme';
import { Logo } from '@anyroad/logo';

export type ErrorPageProps = {
  componentStack?: string | null;
  /** The component stack. */
  dataTestId?: string;
  /** The data-testid attribute. */
  error?: Error | null;
  /* The error object. */
  message?: string | null;
  /** The message to inform a user of an error. */
};

const Container = styled.div({
  alignItems: 'center',
  background: colors.background.light,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  left: 0,
  position: 'fixed',
  top: 0,
  width: '100%',
});

const StyledLogo = styled(Logo)({
  marginBottom: '6rem',
});

const Message = styled.div({
  color: colors.grays['50'],
  textAlign: 'center',
  fontWeight: 600,
  fontSize: '1.5rem',
  marginBottom: '2rem',
});

const ErrorMessage = styled.div({
  background: colors.grays['30'],
  padding: '16px',
  border: `1px solid ${colors.background.dark}`,
  width: '360px',
});

/**
 * ErrorPage component.
 *
 * @type {React.FC<ErrorPageProps>}
 * @returns {React.ReactElement} The error-page.
 * @example
 * import { ErrorPage } from './ErrorPage';
 */
export const ErrorPage = forwardRef<HTMLDivElement, ErrorPageProps>(
  ({ dataTestId, error, message, componentStack }, ref) => (
    <Container data-testid={dataTestId} ref={ref}>
      <StyledLogo type="lightbg" />
      <Message>{message}</Message>
      {typeof message !== 'undefined' && (
        <ErrorMessage data-testid="error-message">
          <pre>{error?.message}</pre>
          <pre>{componentStack}</pre>
        </ErrorMessage>
      )}
    </Container>
  )
);

ErrorPage.displayName = 'ErrorPage';
